<!--
   Generic component for icons with specific hover behaviour - color change + icon type change

    <IconWithHover
        :color="..."    # color of icon - also decides hover color (either same color or depending on a mappiong table)
        :icon="..."     # icon to use - as thing in regular state and solid when hovered
        :dark="..."     # is the icon for dark background - if true, icon is solid instead of thin
        :hover-color="..." # color name to use on hover if not the default ones
        any other props can be passed and will be sent to the v-icon component

    />

    Emits:
    - onhover : emitted when hovered
-->

<script setup lang="ts">
import { computed } from 'vue';

/* PROPS */
type Props = {
  icon: string;
  color?: string;
  dark?: boolean;
  hoverColor?: string;
};
const props = withDefaults(defineProps<Props>(), {
  color: 'icon',
  iconType: 'fat',
  dark: false
});

// mapping for hover color depending on initial color - if no specific hover color use initial color
const colorConfig = {
  primaryIcon: 'primaryIconHover',
  secondaryIcon: 'secondaryIconHover'
  // add more data if other colors that are different with / without hover
};

const iconType = computed(() => (!props.dark ? 'fat' : 'fas'));
const hoverColor = props.hoverColor ?? colorConfig[props.color] ?? props.color; // Hover color is either hover color given, or color from mapping table if any, or initial color

/**
 * Will emits an event while hovered
 *
 * @param {Boolean} isHover - true if hovered, false otherwise
 */
function onHover(isHover) {
  emit('onhover', isHover);
}

const emit = defineEmits(['onhover']);
</script>

<template>
  <v-hover v-slot="{ isHovering, props: hoverProps }" @update:modelValue="onHover">
    <v-icon
      v-bind="{ ...$attrs, ...hoverProps }"
      :icon="`${!isHovering ? iconType : 'fas'} ${props.icon}`"
      :color="!isHovering ? props.color : hoverColor"
      class="pointer"
    />
  </v-hover>
</template>
